// pages/UserManagement/AddNewUser.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import { getCurrentUser, getAuthToken } from "../../utils/AuthHelper";
import axios from "axios";
import { backend_url } from "../../App";

export default function AddNewUser() {
  const user = getCurrentUser();
  const token = getAuthToken();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
    user_type: "user",
    organization:
      user?.user_type === "admin" ? "" : user?.organization?.id || "",
    is_active: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const headers = { Authorization: `Token ${token}` };
        const res = await axios.get(
          `${backend_url}/accounts/api/organizations/`,
          { headers },
        );
        setOrganizations(res.data.results || res.data);
      } catch (err) {
        console.error("Error fetching organizations:", err);
      }
    };

    if (user?.user_type === "admin") {
      fetchOrganizations();
    }
  }, [user?.user_type, token]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = "Please confirm password";
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    if (
      user.user_type === "admin" &&
      !formData.organization &&
      formData.user_type !== "admin"
    ) {
      newErrors.organization = "Organization is required for non-admin users";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };

      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        re_password: formData.password, // For Djoser
        user_type: formData.user_type,
        is_active: formData.is_active,
      };

      // Add organization based on user type
      if (user.user_type === "admin") {
        if (formData.organization && formData.user_type !== "admin") {
          payload.organization = formData.organization;
        }
      } else if (user.user_type === "superuser") {
        // Superuser can only create regular users in their organization
        payload.user_type = "user";
        payload.organization = user.organization.id;
      }

      await axios.post(`${backend_url}/accounts/api/users/`, payload, {
        headers,
      });

      setSuccess("User created successfully!");

      // Reset form
      setFormData({
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        password: "",
        confirm_password: "",
        user_type: user?.user_type === "admin" ? "superuser" : "user",
        organization:
          user?.user_type === "admin" ? "" : user?.organization?.id || "",
        is_active: true,
      });
    } catch (err) {
      console.error("Error creating user:", err);

      if (err.response?.data) {
        // Handle field-specific errors
        const errorData = err.response.data;
        const fieldErrors = {};

        Object.keys(errorData).forEach((key) => {
          if (Array.isArray(errorData[key])) {
            fieldErrors[key] = errorData[key][0];
          } else if (typeof errorData[key] === "string") {
            fieldErrors[key] = errorData[key];
          }
        });

        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          setError(err.response.data.detail || "Failed to create user");
        }
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Grid container spacing={4}>
        {/* Left Column - Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
            >
              <PersonAddIcon />
              Create New User Account
            </Typography>

            <Grid container spacing={3}>
              {/* Username */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOpenIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* User Type (Admin only) */}
              {user.user_type === "admin" && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>User Type</InputLabel>
                    <Select
                      name="user_type"
                      value={formData.user_type}
                      label="User Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="admin">System Administrator</MenuItem>
                      <MenuItem value="superuser">
                        Organization Administrator
                      </MenuItem>
                      <MenuItem value="user">Regular User</MenuItem>
                      <MenuItem value="viewer">Viewer (Read Only)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Organization (Admin only) */}
              {user.user_type === "admin" && formData.user_type !== "admin" && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      name="organization"
                      value={formData.organization}
                      label="Organization"
                      onChange={handleChange}
                      error={!!errors.organization}
                    >
                      <MenuItem value="">Select Organization</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.organization && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        {errors.organization}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* For Superuser - Show organization info */}
              {user.user_type === "superuser" && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are creating a user for your organization:{" "}
                    <strong>{user.organization?.name}</strong>. You can only
                    create Regular Users.
                  </Alert>
                </Grid>
              )}

              {/* Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                    />
                  }
                  label="Active Account"
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ py: 1.5, px: 4 }}
                >
                  {loading ? "Creating User..." : "Create User Account"}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Guidelines */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <BusinessIcon />
                User Creation Guidelines
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {user.user_type === "admin" ? (
                  <>
                    <Typography variant="body2">
                      <strong>System Administrators:</strong> Full access to all
                      features and organizations.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Organization Administrators:</strong> Can manage
                      users and permissions within their assigned organization.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Regular Users:</strong> Can access assigned
                      cameras and species based on permissions.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Viewers:</strong> Read-only access to assigned
                      content.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      As an Organization Administrator, you can only create{" "}
                      <strong>Regular Users</strong> for your organization.
                    </Typography>
                    <Typography variant="body2">
                      New users will inherit the organization:{" "}
                      <strong>{user.organization?.name}</strong>
                    </Typography>
                    <Typography variant="body2">
                      After creation, you can assign species permissions to each
                      user.
                    </Typography>
                  </>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  <strong>Note:</strong> Users will receive their credentials
                  via email if configured.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
